import { useRef, createContext, useCallback, useState, useEffect } from 'react';
import QuickViewV2 from 'components/modules/QuickView/QuickViewV2';
import quickViewEvent from 'lib/analytics/mixpanel/quickViewEvent';
import {
  quickviewReturnMeterState,
  quickviewCloseMeter,
} from 'components/integrations/PianoPlacements/components/MeterCounter/MeterCounter';
import useMarketingHat from 'lib/hooks/useMarketingHat';
import useIntersectionV2 from 'lib/hooks/useIntersectionv2';
import { AnalyticsAttributes } from 'types/analytics';

type ButtonEvent = EventTarget & HTMLButtonElement;
interface IQuickViewContext {
  isOpen: boolean;
  closeQuickView: () => void;
  setContent: (
    id: string | null,
    content: React.ReactElement<any, string | React.JSXElementConstructor<any>>,
    target: ButtonEvent,
    analyticsAttributes?: AnalyticsAttributes,
  ) => void;
  headerVisible: boolean;
}

const initialState: IQuickViewContext = {
  isOpen: false,
  closeQuickView: () => null,
  setContent: () => null,
  headerVisible: false,
};

export const QuickViewContext = createContext<IQuickViewContext>(initialState);

const QuickViewProvider = ({
  children,
}: {
  children: React.ReactElement | React.ReactElement[];
}) => {
  const headerRef = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [quickViewContent, setQuickViewContent] =
    useState<React.ReactElement | null>(null);
  const [triggerButton, setTriggerButton] = useState<ButtonEvent | null>(null);
  const [analyticsAttributes, setAnalyticsAttributes] =
    useState<AnalyticsAttributes | null>(null);
  const [identifier, setIdentifier] = useState<string | null>(null);
  const { segment } = useMarketingHat();
  const isMember = segment === 'multisite' || segment?.startsWith('standard');

  const sendAnalytics = useCallback(
    (clickType: 'open' | 'close') => {
      if (!analyticsAttributes) {
        return;
      }
      const { documentId, documentTitle, documentType, type } =
        analyticsAttributes;
      quickViewEvent(documentId, documentTitle, documentType, type, clickType);
    },
    [analyticsAttributes],
  );

  const setContent = useCallback(
    (
      id: string | null,
      content: React.ReactElement | null,
      target: ButtonEvent | null,
      analyticsAttributes?: AnalyticsAttributes,
    ) => {
      if (identifier === id) {
        setQuickViewContent(null);
        setIdentifier(null);
      } else {
        setQuickViewContent(content);
        setIdentifier(id);
        if (target) {
          setTriggerButton(target);
        }
      }
      if (analyticsAttributes) {
        setAnalyticsAttributes(analyticsAttributes);
      }
    },
    [identifier],
  );

  const closeQuickView = useCallback(() => {
    setContent(null, null, null);
    setIsOpen(false);
    if (triggerButton) {
      triggerButton.focus();
    }
  }, [setContent, triggerButton]);

  useEffect(() => {
    headerRef.current = document.querySelector('.global-nav');
  }, []);

  useEffect(() => {
    const quickViewListener = () => {
      if (quickViewContent) {
        setIsOpen(true);
        sendAnalytics('open');
        if (!isMember) {
          quickviewCloseMeter();
        }
      } else {
        if (!isMember) {
          quickviewReturnMeterState();
        }
        if (analyticsAttributes) {
          setIsOpen(false);
          sendAnalytics('close');
        }
      }
    };
    quickViewListener();
  }, [analyticsAttributes, isMember, quickViewContent, sendAnalytics]);

  const headerVisible = useIntersectionV2(headerRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0,
  });

  const state = {
    isOpen,
    setContent,
    closeQuickView,
    headerVisible,
  };

  return (
    <QuickViewContext.Provider value={state}>
      {isOpen && quickViewContent ? (
        <QuickViewV2 quickViewContent={quickViewContent} />
      ) : null}
      {children}
    </QuickViewContext.Provider>
  );
};

export default QuickViewProvider;
